import type { ReactNode } from 'react';
import React, { useState } from 'react';
import { Tabs, Button, message, Divider, Badge, Grid } from 'antd';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import WorkReportTable from './WorkReportTable';
import {
  rejectReportByTutor,
  validateWorkReportByTutor,
} from '../../APIcalls/APICallsForReview';
import { EvaluationComponentForComputerScience } from '../EvaluationForm/EvaluationComponentForComputerScience';
import { EvaluationComponentForMathAndMathComputerScience } from '../EvaluationForm/EvaluationComponentForMathAndMathComputerScience';
import { GetInternshipProcessesByUserId } from '../../helper/apiHelper';
import type { InternshipProcessWithNames } from '../../Types/interfaces';
import { Roles, StepStatus, StudyLineType } from '../../Types/interfaces';
import { useTranslation } from 'react-i18next';
import ConventionSignComponent from '../ConventionSignComponent';
import { EditableCompetencyTable } from '../InternshipManagement/EditableCompetencyTable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '../../Constants/queryKeys';
import { useCurrentUser } from '../../hooks/useCurrentUser';
import { EvaluationChooserComponent } from '../EvaluationForm/EvaluationChooserComponent';
import { EvaluationReportDisplay } from '../EvaluationForm/Display/EvaluationReportDisplayComponent';
import {
  useAcceptedProcesses,
  useEvaluatedProcesses,
  useOngoingProcessesOfTutor,
  useReadyToSignProcessesOfTutor,
  useReadyToSignWorkReportsOfTutor,
  useWaitingForReviewProcesses,
} from '../../hooks/BadgeHooks/ReviewHooks';

const { useBreakpoint } = Grid;
const EMPTY_ARRAY: InternshipProcessWithNames[] = [];

const ReviewComponent = (): React.ReactElement => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [currentKey, setCurrentKey] = useState<string>();
  const [acceptedReportKey, setAcceptedReportKey] = useState<string>();
  const [file, setFile] = useState<File | null>(null);
  const [messageApi, contextHolder] = message.useMessage();
  const queryClient = useQueryClient();
  const screens = useBreakpoint();

  const { data: processes = EMPTY_ARRAY } = useQuery({
    queryKey: [...QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID, loggedInUser?.id],
    queryFn: async () => {
      return await GetInternshipProcessesByUserId(
        loggedInUser?.id?.toString() ?? ''
      );
    },
    enabled: !!loggedInUser?.id,
  });

  const readyToSignConventions = useReadyToSignProcessesOfTutor(processes);
  const ongoingProcesses = useOngoingProcessesOfTutor(processes);
  const waitingForReviewProcesses = useWaitingForReviewProcesses(processes);
  const workReportsToSign = useReadyToSignWorkReportsOfTutor(processes);
  const acceptedProcesses = useAcceptedProcesses(processes);
  const { data: evaluatedProcesses = [] } = useEvaluatedProcesses(processes);

  const editableCompetencyTableIds: number[] = processes
    .filter((process) =>
      process.processSteps.some(
        (step) =>
          step.stepName === 'ConventionFillingAndSigningByStudent' &&
          step.stepStatus !== 3
      )
    )
    .map((process) => process.competencyTable.id ?? 0);

  const handleRejectReport = async (worklogId: number): Promise<void> => {
    try {
      await axios.post(rejectReportByTutor(worklogId));
      messageApi.success(t('reviewComp.rejectSuccess'));
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
      });
    } catch (error) {
      console.error(error);
      messageApi.error(t('reviewComp.rejectError'));
    }
  };

  const handleValidateWorkReport = async (
    process: InternshipProcessWithNames
  ): Promise<void> => {
    try {
      await axios.post(
        validateWorkReportByTutor(
          process.organizationId,
          process.id,
          loggedInUser?.id ?? 0
        )
      );
      messageApi.success(t('reviewComp.validateSuccess'));
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
      });
    } catch (error) {
      console.error(error);
      messageApi.error(t('reviewComp.validateError'));
    }
  };

  const GetEvaluationComponentForProcess = (
    process: InternshipProcessWithNames
  ): ReactNode => {
    switch (process.studyLineType) {
      case StudyLineType.ComputerScience:
        return (
          <EvaluationComponentForComputerScience
            process={process}
            evaluationFinished={async () => {
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
              });
            }}
          ></EvaluationComponentForComputerScience>
        );

      case StudyLineType.Math:
        return (
          <EvaluationComponentForMathAndMathComputerScience
            process={process}
            evaluationFinished={() => {
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
              });
              queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.EVALUATED_PROCESSES,
              });
            }}
          ></EvaluationComponentForMathAndMathComputerScience>
        );
      case StudyLineType.MathComputerScience:
        return <EvaluationChooserComponent process={process} />;
      default:
        return <div>{t('reviewComp.noEvaluationForm')}</div>;
    }
  };

  const studentsTab = (): React.ReactElement => {
    return (
      <Tabs
        tabPosition={screens.sm ? 'left' : 'top'}
        items={processes?.map((process: InternshipProcessWithNames) => {
          const editableCompetencyTable: boolean =
            editableCompetencyTableIds?.some(
              (id) => id === process.competencyTable.id
            ) ?? false;
          return {
            key: String(process.id),
            label: process.studentName,
            children: (
              <EditableCompetencyTable
                processId={process.id}
                isEditableTable={editableCompetencyTable}
              />
            ),
          };
        })}
      />
    );
  };

  return (
    <>
      {contextHolder}
      <Tabs
        centered
        type="card"
        defaultActiveKey="2"
        activeKey={currentKey}
        onTabClick={(key) => {
          setCurrentKey(key);
        }}
        style={{ paddingTop: 10 }}
        items={[
          {
            key: '1',
            label: (
              <span>
                {t('reviewComp.conventions')}{' '}
                <Badge
                  count={readyToSignConventions.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <ConventionSignComponent
                menuGroup="Tutor"
                recount={async () => {
                  queryClient.invalidateQueries({
                    queryKey: QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
                  });
                  queryClient.invalidateQueries({
                    queryKey: QUERY_KEYS.EVALUATED_PROCESSES,
                  });
                }}
                role={Roles.Tutor}
              />
            ),
          },
          {
            key: '2',
            label: <span>{t('reviewComp.competencyTable')} </span>,
            children: studentsTab(),
          },
          {
            key: '3',
            label: <span>{t('reviewComp.ongoingReports')} </span>,
            children: (
              <Tabs
                tabPosition={screens.md ? 'left' : 'top'}
                items={ongoingProcesses.map(
                  (process: InternshipProcessWithNames) => {
                    return {
                      key: String(process.id),
                      label: process.studentName,
                      children: (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.worklogs')}
                          </Divider>
                          <WorkReportTable
                            WorkLogId={process.workLogId}
                            WorkLogDocumentUrl={process.workLogDocumentUrl}
                            editable={true}
                            signable={false}
                            role={Roles.Tutor}
                            onSubmit={false}
                          />
                        </>
                      ),
                    };
                  }
                )}
              />
            ),
          },
          {
            key: '4',
            label: (
              <span>
                {t('reviewComp.waitingReports')}{' '}
                <Badge
                  count={waitingForReviewProcesses.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <Tabs
                tabPosition={screens.md ? 'left' : 'top'}
                items={waitingForReviewProcesses.map(
                  (process: InternshipProcessWithNames) => {
                    return {
                      key: String(process.id),
                      label: process.studentName,
                      children: (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.worklogs')}
                          </Divider>
                          <WorkReportTable
                            StudentId={process.studentId}
                            ProcessId={process.id}
                            WorkLogId={process.workLogId}
                            WorkLogDocumentUrl={process.workLogDocumentUrl}
                            WorkLogDocumentStatus={
                              process.currentOrganizationStepName
                            }
                            editable={true}
                            signable={false}
                            onSubmit={false}
                            workReport={file}
                            setWorkReport={setFile}
                            role={Roles.Tutor}
                          />
                          <br />
                          <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            style={{
                              position: 'relative',
                              left: '50%',
                              transform: 'translateX(-50%)',
                            }}
                            onClick={() => handleValidateWorkReport(process)}
                          >
                            {t('reviewComp.acceptReport')}
                          </Button>
                          <br />
                          <Button
                            type="primary"
                            icon={<ArrowLeftOutlined />}
                            style={{
                              position: 'relative',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              marginTop: 10,
                            }}
                            onClick={() =>
                              handleRejectReport(process.workLogId)
                            }
                          >
                            {t('reviewComp.rejectReport')}
                          </Button>
                          <br />
                        </>
                      ),
                    };
                  }
                )}
              />
            ),
          },
          {
            key: '5',
            label: (
              <span>
                {t('reviewComp.workReportsToSign')}{' '}
                <Badge
                  count={workReportsToSign.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <Tabs
                tabPosition={screens.md ? 'left' : 'top'}
                items={workReportsToSign.map(
                  (process: InternshipProcessWithNames) => {
                    return {
                      key: String(process.id),
                      label: process.studentName,
                      children: (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.worklogs')}
                          </Divider>
                          <WorkReportTable
                            StudentId={process.studentId}
                            ProcessId={process.id}
                            WorkLogId={process.workLogId}
                            WorkLogDocumentUrl={process.workLogDocumentUrl}
                            WorkLogDocumentStatus={
                              process.currentOrganizationStepName
                            }
                            editable={false}
                            signable={true}
                            onSubmit={false}
                            workReport={file}
                            setWorkReport={setFile}
                            role={Roles.Tutor}
                            onFileUploadSuccess={async () => {
                              queryClient.invalidateQueries({
                                queryKey:
                                  QUERY_KEYS.INTERNSHIP_PROCESSES_BY_USER_ID,
                              });
                              queryClient.invalidateQueries({
                                queryKey: QUERY_KEYS.EVALUATED_PROCESSES,
                              });
                            }}
                            alreadySigned={
                              process.processSteps?.find(
                                (e) => e.stepName === 'SigningWorkReportByTutor'
                              )?.stepStatus === StepStatus.updated
                            }
                          />
                        </>
                      ),
                    };
                  }
                )}
              />
            ),
          },
          {
            key: '6',
            label: (
              <span>
                {t('reviewComp.acceptedReports')}{' '}
                <Badge
                  count={acceptedProcesses.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <Tabs
                tabPosition={screens.md ? 'left' : 'top'}
                activeKey={acceptedReportKey}
                onTabClick={(key) => setAcceptedReportKey(key)}
                items={acceptedProcesses.map(
                  (process: InternshipProcessWithNames) => {
                    return {
                      key: String(process.id),
                      label: process.studentName,
                      children: (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.worklogs')}
                          </Divider>
                          <WorkReportTable
                            WorkLogId={process.workLogId}
                            WorkLogDocumentUrl={process.workLogDocumentUrl}
                            WorkLogDocumentStatus={
                              process.currentOrganizationStepName
                            }
                            role={Roles.Tutor}
                            editable={false}
                            signable={false}
                            onSubmit={false}
                          />
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.fillEvalReport')}
                          </Divider>

                          {GetEvaluationComponentForProcess(process)}
                        </>
                      ),
                    };
                  }
                )}
              />
            ),
          },
          {
            key: '7',
            label: (
              <span>
                {t('reviewComp.evaluatedReports')}{' '}
                <Badge
                  count={evaluatedProcesses.length}
                  overflowCount={10}
                  size="small"
                />
              </span>
            ),
            children: (
              <Tabs
                tabPosition={screens.md ? 'left' : 'top'}
                items={evaluatedProcesses.map(
                  (process: InternshipProcessWithNames) => {
                    return {
                      key: String(process.id),
                      label: process.studentName,
                      children: (
                        <>
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.worklogs')}
                          </Divider>
                          <WorkReportTable
                            WorkLogId={process.workLogId}
                            WorkLogDocumentUrl={process.workLogDocumentUrl}
                            WorkLogDocumentStatus={
                              process.currentOrganizationStepName
                            }
                            role={Roles.Tutor}
                            editable={false}
                            signable={false}
                            onSubmit={false}
                            alreadySigned={false}
                          />
                          <Divider plain style={{ fontSize: 20 }}>
                            {t('reviewComp.evalReport')}
                          </Divider>
                          <EvaluationReportDisplay
                            organizationId={process.organizationId}
                            reportId={process.evaluationReportId}
                            studentName={process.studentName
                              .split(' ')
                              .join('')}
                            isSignable={true}
                          />
                        </>
                      ),
                    };
                  }
                )}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default ReviewComponent;
